import { useEffect, useState } from "react";
import { Alert, Grid, Skeleton, Typography, useTheme } from "@mui/material";
import { LineChart } from "@mui/x-charts";
import { getSyncChannelResults } from "../utils/dataLoader";

// One channel's detailed sync curve. Fetches its own metrics on mount and
// aborts on unmount, so paging away drops the data along with the chart.
export default function SyncChannelChart({
  caseId,
  clipId,
  channelNumber,
  isReference,
  showFailure,
  yRange,
}) {
  const theme = useTheme();
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setMetrics(null);
    setError(false);

    getSyncChannelResults(
      caseId,
      clipId,
      channelNumber,
      controller.signal,
    ).then((result) => {
      if (controller.signal.aborted) return;
      if (result?.metrics) {
        setMetrics(result.metrics);
      } else {
        setError(true);
      }
    });

    return () => controller.abort();
  }, [caseId, clipId, channelNumber]);

  return (
    <Grid
      borderRadius={1}
      boxShadow={2}
      display="flex"
      flexDirection="column"
      height={400}
      outline={showFailure ? `1px solid ${theme.palette.error.main}` : "none"}
      overflow="hidden"
      p={3}
      size={{
        xs: 12,
        md: 6,
        xl: 4,
      }}
    >
      <Typography gutterBottom textTransform="uppercase" variant="caption">
        Channel {channelNumber + 1} {isReference && "(Reference)"}
      </Typography>
      {error ? (
        <Alert severity="error">Could not load results for this channel.</Alert>
      ) : !metrics ? (
        <Skeleton variant="rounded" sx={{ flex: 1 }} />
      ) : (
        <LineChart
          grid={{ vertical: true, horizontal: true }}
          margin={{ bottom: 0, left: -8 }}
          xAxis={[
            {
              data: metrics.map((v) => v[0]),
              label: "TIME OFFSET (SECONDS)",
            },
          ]}
          yAxis={[
            {
              domainLimit: () => ({
                min: yRange[0],
                max: yRange[1],
              }),
            },
          ]}
          series={[
            {
              data: metrics.map((v) => v[1]),
              showMark: false,
              color: showFailure
                ? theme.palette.error.main
                : theme.palette.info.main,
            },
          ]}
        />
      )}
    </Grid>
  );
}
